import { useState, useEffect, useCallback } from 'react';
import {
  DataGridBody,
  DataGridRow,
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  createTableColumn,
  makeStyles,
  Button,
  Dropdown,
  Option,
  Spinner,
} from '@fluentui/react-components';
import {
  ArrowClockwiseRegular,
  ChevronLeftRegular,
  ChevronRightRegular,
  ArrowDownloadRegular,
} from '@fluentui/react-icons';
import { fetchAllAlerts } from '../services/localApi';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const CSV_EXPORT_LIMIT = 1000;

const dateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Montreal',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const parseUtcDate = (isoString) => {
  if (!isoString) return null;
  const normalized = /([Z+-])/.test(isoString.slice(-6)) ? isoString : isoString + 'Z';
  const date = new Date(normalized);
  return isNaN(date.getTime()) ? null : date;
};

const formatAlertDate = (isoString) => {
  const date = parseUtcDate(isoString);
  if (!date) return null;
  const parts = dateFormatter.formatToParts(date);
  const get = (type) => parts.find(p => p.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`;
};

const escapeCsvField = (value) => {
  const str = String(value ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const buildCsv = (alerts) => {
  const headers = ['Équipement', 'Date de création', 'Confirmation', 'Recette 1', 'Recette 2'];
  const rows = alerts.map((a) => [
    escapeCsvField(a.sEquip),
    escapeCsvField(formatAlertDate(a.createdAt) ?? ''),
    escapeCsvField(formatAlertDate(a.acknowledgedAt) ?? ''),
    escapeCsvField(a.sRecette1),
    escapeCsvField(a.sRecette2),
  ].join(','));
  return [headers.join(','), ...rows].join('\n');
};

const useStyles = makeStyles({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '12px',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pageSizeDropdown: {
    minWidth: '80px',
  },
  paginationBar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '12px',
    paddingTop: '12px',
  },
  pageInfo: {
    fontSize: '13px',
    color: '#616161',
  },
  pageSizeLabel: {
    fontSize: '13px',
    color: '#616161',
  },
});

const makeItems = (alerts) => {
  return alerts.map((alert) => ({
    id: alert.id,
    equipment: { label: alert.sEquip ?? 'N/A' },
    createdAt: {
      label: formatAlertDate(alert.createdAt) ?? 'N/A',
      timestamp: parseUtcDate(alert.createdAt)?.getTime() ?? 0,
    },
    acknowledgedAt: {
      label: formatAlertDate(alert.acknowledgedAt) ?? '\u2014',
      timestamp: parseUtcDate(alert.acknowledgedAt)?.getTime() ?? 0,
    },
    recipe1: { label: alert.sRecette1 ?? 'N/A' },
    recipe2: { label: alert.sRecette2 ?? 'N/A' },
  }));
};

const columns = [
  createTableColumn({
    columnId: 'equipment',
    compare: (a, b) => a.equipment.label.localeCompare(b.equipment.label),
    renderHeaderCell: () => <strong>Équipement</strong>,
    renderCell: (item) => item.equipment.label,
  }),
  createTableColumn({
    columnId: 'createdAt',
    compare: (a, b) => a.createdAt.timestamp - b.createdAt.timestamp,
    renderHeaderCell: () => <strong>Date de création</strong>,
    renderCell: (item) => item.createdAt.label,
  }),
  createTableColumn({
    columnId: 'acknowledgedAt',
    compare: (a, b) => a.acknowledgedAt.timestamp - b.acknowledgedAt.timestamp,
    renderHeaderCell: () => <strong>Confirmation</strong>,
    renderCell: (item) => item.acknowledgedAt.label,
  }),
  createTableColumn({
    columnId: 'recipe1',
    compare: (a, b) => a.recipe1.label.localeCompare(b.recipe1.label),
    renderHeaderCell: () => <strong>Recette 1</strong>,
    renderCell: (item) => item.recipe1.label,
  }),
  createTableColumn({
    columnId: 'recipe2',
    compare: (a, b) => a.recipe2.label.localeCompare(b.recipe2.label),
    renderHeaderCell: () => <strong>Recette 2</strong>,
    renderCell: (item) => item.recipe2.label,
  }),
];

const columnSizingOptions = {
  equipment: { idealWidth: 160 },
  createdAt: { idealWidth: 160 },
  acknowledgedAt: { idealWidth: 160 },
  recipe1: { idealWidth: 160 },
  recipe2: { idealWidth: 160 },
};

const AlertsTable = ({ isOpen }) => {
  const styles = useStyles();
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.floor(offset / pageSize) + 1;

  const loadPage = useCallback(async (newOffset, limit) => {
    setIsLoading(true);
    try {
      const data = await fetchAllAlerts({ limit, offset: newOffset });
      setAlerts(data.alerts);
      setTotal(data.total);
      setOffset(newOffset);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setOffset(0);
      loadPage(0, pageSize);
    }
  }, [isOpen, loadPage, pageSize]);

  const handlePageSizeChange = (_, data) => {
    const newSize = parseInt(data.optionValue);
    setPageSize(newSize);
    setOffset(0);
    loadPage(0, newSize);
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const data = await fetchAllAlerts({ limit: CSV_EXPORT_LIMIT, offset: 0 });
      const csv = buildCsv(data.alerts);
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `alertes_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export alerts:', err);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading && alerts.length === 0) {
    return <Spinner size="medium" label="Chargement..." />;
  }

  return (
    <>
      <div className={styles.toolbar}>
        <Button
          appearance="subtle"
          icon={isExporting ? <Spinner size="tiny" /> : <ArrowDownloadRegular />}
          disabled={isExporting || total === 0}
          onClick={handleExportCsv}
        >
          Exporter CSV
        </Button>
        <div className={styles.toolbarRight}>
          <span className={styles.pageSizeLabel}>Alertes par page</span>
          <Dropdown
            className={styles.pageSizeDropdown}
            value={String(pageSize)}
            selectedOptions={[String(pageSize)]}
            onOptionSelect={handlePageSizeChange}
            size="small"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <Option key={size} value={String(size)}>{String(size)}</Option>
            ))}
          </Dropdown>
          <Button
            appearance="subtle"
            icon={<ArrowClockwiseRegular />}
            disabled={isLoading}
            onClick={() => loadPage(offset, pageSize)}
          />
        </div>
      </div>
      <DataGrid
        items={makeItems(alerts)}
        columns={columns}
        sortable
        getRowId={(item) => item.id}
        resizableColumns
        columnSizingOptions={columnSizingOptions}
        resizableColumnsOptions={{ autoFitColumns: true }}
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody>
          {({ item, rowId }) => (
            <DataGridRow key={rowId}>
              {({ renderCell }) => (
                <DataGridCell>{renderCell(item)}</DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
      {total > pageSize && (
        <div className={styles.paginationBar}>
          <Button
            appearance="subtle"
            icon={<ChevronLeftRegular />}
            disabled={offset === 0 || isLoading}
            onClick={() => loadPage(offset - pageSize, pageSize)}
          />
          <span className={styles.pageInfo}>
            Page {currentPage} de {totalPages}
          </span>
          <Button
            appearance="subtle"
            icon={<ChevronRightRegular />}
            disabled={offset + pageSize >= total || isLoading}
            onClick={() => loadPage(offset + pageSize, pageSize)}
          />
        </div>
      )}
    </>
  );
};

export default AlertsTable;
